import React, { useCallback, useRef, useState } from 'react';
import { Copy, ExternalLink, FileOutput, Link, Search, Send, TextCursorInput } from 'lucide-react';
import { isMacPlatform } from '../../utils/keyboard';
import { openExternal } from '../../utils/openExternal';
import { useExtensionStore } from '../../extensions/store';
import { openRequestDraft } from '../../store/requestDraftOpen';
import { isRequestUrl } from './terminalLinks';
import { copyToClipboard } from './terminalCopy';
import { isFindChord } from './terminalSearch';
import { usePaneCopy } from './usePaneCopy';
import { usePaneCommandNotify } from './usePaneCommandNotify';

const NO_API = { getTerm: () => null, getSearch: () => null, getPrompts: () => [], getLink: () => null };

/**
 * A pane's own tools on its xterm: the find bar (card 2), the copy chords,
 * copy mode and "Copy last output" (card 3), links (card 4), the
 * right-click menu that offers them, and "command finished" (card 5). The pane body hands its xterm
 * accessors in through `onApi`.
 *
 * `onKey(e)` runs first in the pane's key filter; true = handled here, keep
 * the key from the shell.
 */
export function usePaneTools(t, pane = {}) {
  const api = useRef(NO_API);
  const notify = usePaneCommandNotify(t, pane);
  const onApi = useCallback(
    (next) => {
      api.current = { ...NO_API, ...next };
      next.setOnCommandDone?.(notify);
    },
    [notify]
  );
  const getTerm = useCallback(() => api.current.getTerm(), []);
  const getSearch = useCallback(() => api.current.getSearch(), []);
  const getPrompts = useCallback(() => api.current.getPrompts(), []);

  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const copy = usePaneCopy({ getTerm, getPrompts });
  const apiClientOn = useExtensionStore((s) => s.apiClientEnabled !== false);
  /** The link under the pointer — read when the menu opens (the hover ends on the way to it). */
  const hoveredLink = useCallback(() => api.current.getLink(), []);

  const onKey = useCallback(
    (e) => {
      if (copy.onKey(e)) return true;
      if (isFindChord(e, isMacPlatform())) {
        e.preventDefault();
        e.stopPropagation();
        setSearchOpen(true);
        return true;
      }
      return false;
    },
    [copy]
  );

  const mac = isMacPlatform();
  /** Open link / Copy link / Open as request, for a right-click on a link. */
  const linkItems = (link) =>
    link
      ? [
          { icon: <ExternalLink size={14} />, label: t('terminalExt.openLink', 'Open link'), hint: mac ? '⌘+click' : 'Ctrl+click', onClick: () => openExternal(link) },
          { icon: <Link size={14} />, label: t('terminalExt.copyLink', 'Copy link'), onClick: () => copyToClipboard(link) },
          apiClientOn && isRequestUrl(link) && { icon: <Send size={14} />, label: t('terminalExt.openAsRequest', 'Open as request'), onClick: () => openRequestDraft({ method: 'GET', url: link }) },
          { separator: true },
        ].filter(Boolean)
      : [];

  /** The terminal's right-click menu: link items, these, then the pane's own items. */
  const bodyMenuItems = (link) => {
    const term = getTerm();
    return [
      ...linkItems(link),
      { icon: <Copy size={14} />, label: t('terminalExt.copy', 'Copy'), hint: mac ? '⌘C' : 'Ctrl+Shift+C', disabled: !term?.hasSelection(), onClick: () => copy.copySelection() },
      { icon: <FileOutput size={14} />, label: t('terminalExt.copyLastOutput', 'Copy last output'), disabled: !copy.canCopyLastOutput(), onClick: () => copy.copyLastOutput() },
      { icon: <Search size={14} />, label: t('terminalExt.find', 'Find…'), hint: mac ? '⌘F' : 'Ctrl+F', onClick: openSearch },
      { icon: <TextCursorInput size={14} />, label: t('terminalExt.copyMode', 'Copy mode'), hint: mac ? '⌘⇧X' : 'Ctrl+Shift+X', onClick: () => copy.enterCopyMode() },
      { separator: true },
    ];
  };

  return { onApi, getTerm, getSearch, searchOpen, openSearch, closeSearch, copy, onKey, bodyMenuItems, hoveredLink };
}
